/**
 * Numbers, money, dates and PO numbers as a PO template prints them
 * (z8nrz7f7dz). Everything goes through Intl with the TEMPLATE's locale, not
 * the browser's: a Belgian tenant's order reads "1,00 €" and 23/12/2025 on
 * every screen that prints it.
 */

import type { PoTemplateLayout } from '@/types/purchaseOrders'

type Numbering = PoTemplateLayout['document']['numbering']

/** Intl throws on a malformed tag; a template typo must not blank the page. */
function safeLocale(locale: string): string {
  try {
    return Intl.getCanonicalLocales(locale)[0] ?? 'en-GB'
  } catch {
    return 'en-GB'
  }
}

export function formatPoMoney(n: number | null | undefined, currency: string, locale: string): string {
  if (n == null) return '—'
  try {
    return new Intl.NumberFormat(safeLocale(locale), { style: 'currency', currency }).format(n)
  } catch {
    // An unknown currency code: the figure, then the code as typed.
    return `${formatPoNumberValue(n, locale, { min: 2, max: 2 })} ${currency}`
  }
}

/** A plain figure, e.g. a quantity: no forced decimals, at most three. */
export function formatPoNumberValue(
  n: number | null | undefined,
  locale: string,
  digits: { min?: number; max?: number } = {},
): string {
  if (n == null) return '—'
  return new Intl.NumberFormat(safeLocale(locale), {
    minimumFractionDigits: digits.min ?? 0,
    maximumFractionDigits: digits.max ?? 3,
  }).format(n)
}

export function formatPoPercent(n: number | null | undefined, locale: string): string {
  if (n == null) return '—'
  return `${formatPoNumberValue(n, locale, { max: 2 })}%`
}

/**
 * A date in the template's locale, e.g. 23/12/2025 for fr-BE. A bare
 * `YYYY-MM-DD` is a calendar day and is read as such, not as UTC midnight —
 * otherwise it prints as the day before anywhere west of Greenwich.
 */
export function formatPoDateValue(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat(safeLocale(locale), { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
}

/**
 * A PO number under the template's numbering, e.g. PO-26-0001 or
 * ARN-1225-514. Mirrors cgt-backend src/inventory/purchase-orders/template/
 * po-template.rules.ts (formatPoNumber), which mints the real one;
 * here it only previews what the next number will look like. Padding is a
 * minimum width, never a cap.
 */
export function formatPoNumber(numbering: Numbering, seq: number, date: Date): string {
  // UTC, as the backend mints it (po-template.rules.ts).
  const yyyy = String(date.getUTCFullYear())
  const yy = yyyy.slice(-2)
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
  const datePart: Record<Numbering['datePart'], string> = {
    none: '',
    YY: yy,
    YYYY: yyyy,
    MMYY: `${mm}${yy}`,
    YYMM: `${yy}${mm}`,
  }
  const parts = [numbering.prefix, datePart[numbering.datePart], String(seq).padStart(Math.max(0, numbering.padding), '0')]
  return parts.filter((p) => p !== '').join(numbering.separator)
}
